import React, { useState, useMemo, useEffect } from 'react';
import { FinancialConfig, calculateDivision296Tax } from './FinancialConfig.js';
import { SMSFExportEngine } from './SMSFExportEngine';
import {
    calculateDivision296FundEarnings,
    deriveAssessableContributionsFromSmsfState,
    deriveDivision296TsbFromSmsfState,
} from './division296FundEarnings.js';
import CalculationTransparency from './components/CalculationTransparency.js';

const fmt = (n) =>
    n == null
        ? '$0'
        : n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

const fmtPct = (n) => `${(n * 100).toFixed(2)}%`;

const CURRENT_FY = '2026-27';
const THRESHOLD = FinancialConfig.DIVISION_296.THRESHOLD;
const VERY_LARGE_THRESHOLD = FinancialConfig.DIVISION_296.VERY_LARGE_THRESHOLD;
const TAX_RATE = FinancialConfig.DIVISION_296.TAX_RATE;
const VLSBT_RATE = FinancialConfig.DIVISION_296.ADDITIONAL_RATE_ABOVE_VLSBT;
const RELEASE_MAX = FinancialConfig.DIVISION_296.RELEASE_AUTHORITY_LIMIT;

const SMSFDivision296Calculator = ({ smsfState }) => {
    const prefilledAssessable = deriveAssessableContributionsFromSmsfState(smsfState);
    const prefilledTsb = deriveDivision296TsbFromSmsfState(smsfState);

    const [inputs, setInputs] = useState(() => ({
        tsbStart: prefilledTsb.tsbEnd > 0 ? prefilledTsb.tsbStart : 3200000,
        tsbEnd: prefilledTsb.tsbEnd > 0 ? prefilledTsb.tsbEnd : 3450000,
        contributions: prefilledAssessable || 30000,
        benefitsPaid: 0,
        division296Earnings: 220000,
        earningsMode: 'fund_builder',
        taxableIncome: 247500,
        assessableContributions: prefilledAssessable || 30000,
        grossEcpi: 0,
        ecpiNonDeductibleExpenses: 0,
        releasePercent: 85,
    }));

    const [activeTab, setActiveTab] = useState('calculator');

    useEffect(() => {
        const tsb = deriveDivision296TsbFromSmsfState(smsfState);
        const cc = deriveAssessableContributionsFromSmsfState(smsfState);
        if (tsb.tsbEnd <= 0 && !cc) return;
        setInputs((prev) => ({
            ...prev,
            ...(tsb.tsbEnd > 0 ? { tsbStart: tsb.tsbStart, tsbEnd: tsb.tsbEnd } : {}),
            ...(cc > 0 ? { contributions: cc, assessableContributions: cc } : {}),
        }));
    }, [smsfState]);

    const set = (field, raw) => {
        const val = parseFloat(String(raw).replace(/[^0-9.]/g, '')) || 0;
        setInputs(prev => ({ ...prev, [field]: val }));
    };

    const setSigned = (field, raw) => {
        const cleaned = String(raw).replace(/[^0-9.-]/g, '');
        const val = parseFloat(cleaned);
        setInputs(prev => ({ ...prev, [field]: Number.isNaN(val) ? 0 : val }));
    };

    const fundEarningsResult = useMemo(() => {
        if (inputs.earningsMode !== 'fund_builder') return null;
        return calculateDivision296FundEarnings({
            taxableIncome: inputs.taxableIncome,
            assessableContributions: inputs.assessableContributions,
            grossEcpi: inputs.grossEcpi,
            ecpiNonDeductibleExpenses: inputs.ecpiNonDeductibleExpenses,
            financialYear: CURRENT_FY,
        });
    }, [
        inputs.earningsMode,
        inputs.taxableIncome,
        inputs.assessableContributions,
        inputs.grossEcpi,
        inputs.ecpiNonDeductibleExpenses,
    ]);

    const calcOptions = useMemo(() => {
        if (inputs.earningsMode === 'fund_builder') {
            return {
                division296Earnings: fundEarningsResult?.earnings ?? 0,
                useTsbProxy: false,
                financialYear: CURRENT_FY,
                earningsSource: 'fund_builder',
                planningMode: true,
            };
        }
        return {
            division296Earnings: inputs.earningsMode === 'reported' ? inputs.division296Earnings : null,
            useTsbProxy: inputs.earningsMode === 'proxy',
            financialYear: CURRENT_FY,
            planningMode: true,
        };
    }, [inputs.earningsMode, inputs.division296Earnings, fundEarningsResult?.earnings]);

    const result = useMemo(() =>
        calculateDivision296Tax(
            inputs.tsbStart,
            inputs.tsbEnd,
            inputs.contributions,
            inputs.benefitsPaid,
            calcOptions
        ), [inputs.tsbStart, inputs.tsbEnd, inputs.contributions, inputs.benefitsPaid, calcOptions]);

    const earningsModeLabel = {
        reported: 'Reported earnings',
        fund_builder: 'Fund earnings (built)',
        proxy: 'Proxy earnings',
    }[inputs.earningsMode] || 'Earnings';

    const earningsSubLabel = {
        reported: 'Division 296 fund earnings',
        fund_builder: 'From SMSF return worksheet',
        proxy: 'TSB movement net of contributions',
    }[inputs.earningsMode] || '';

    const releaseAmount = Math.min(result.taxAmount * (inputs.releasePercent / 100),
                                     result.taxAmount * RELEASE_MAX);
    const personalPayment = result.taxAmount - releaseAmount;

    const BreakdownRow = ({ label, value, sign }) => (
        <div className="flex justify-between text-sm py-1 border-b border-gray-100 last:border-0">
            <span className="text-gray-600">{label}</span>
            <span className="font-medium tabular-nums">
                {sign === 'minus' ? '−' : sign === 'plus' ? '+' : ''}{fmt(Math.abs(value))}
            </span>
        </div>
    );

    const projection = useMemo(() => {
        const rows = [];
        let balance = inputs.tsbEnd;
        const growthRate = 0.07;
        for (let y = 0; y < 5; y++) {
            const fyStart = `${2026 + y}-${String(2027 + y).slice(-2)}`;
            const startBal = balance;
            const grossGrowth = startBal * growthRate;
            const endBal = startBal + grossGrowth + inputs.contributions - inputs.benefitsPaid;
            const calc = calculateDivision296Tax(
                startBal,
                endBal,
                inputs.contributions,
                inputs.benefitsPaid,
                calcOptions
            );
            balance = endBal - calc.taxAmount;
            rows.push({
                fy: fyStart,
                startBal,
                endBal,
                earnings: calc.superEarnings,
                proportion: calc.proportion,
                taxAmount: calc.taxAmount,
                isLiable: calc.isLiable,
            });
        }
        return rows;
    }, [inputs.tsbEnd, inputs.contributions, inputs.benefitsPaid, calcOptions]);

    const InfoBox = ({ children }) => (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            {children}
        </div>
    );

    const WarningBox = ({ children }) => (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
            {children}
        </div>
    );

    const tabs = [
        { id: 'calculator', label: 'Calculator' },
        { id: 'projection', label: '5-Year Projection' },
        { id: 'explainer', label: 'How it works' },
    ];

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-700 to-purple-900 text-white rounded-xl p-6">
                <div className="mb-3 inline-flex items-center gap-2 bg-emerald-500 text-emerald-950 text-xs font-bold px-3 py-1 rounded-full">
                    <span>&#10003;</span> LAW FROM 1 JULY 2026
                </div>
                <h2 className="text-2xl font-bold mb-1">Division 296 Tax Calculator</h2>
                <p className="text-purple-200 text-sm">
                    Models the additional tax on super earnings where Total Super Balance exceeds the large
                    super balance threshold. Enacted as the <em>Treasury Laws Amendment (Building a Stronger and
                    Fairer Super System) Act 2026</em> (Royal Assent 13 March 2026). Applies from the 2026-27
                    financial year. General information only; confirm with the ATO or a licensed adviser.
                </p>
                <div className="mt-3 flex flex-wrap gap-4 text-xs">
                    <span className="bg-purple-600 px-3 py-1 rounded-full">LSBT {CURRENT_FY}: {fmt(THRESHOLD)} (CPI-indexed)</span>
                    <span className="bg-purple-600 px-3 py-1 rounded-full">VLSBT {CURRENT_FY}: {fmt(VERY_LARGE_THRESHOLD)}</span>
                    <span className="bg-purple-600 px-3 py-1 rounded-full">15% above LSBT + 10% above VLSBT</span>
                </div>
            </div>

            <WarningBox>
                <strong>Important:</strong> Division 296 applies to your <em>Total Super Balance across all funds</em>,
                not just your SMSF. Enter <strong>Division 296 fund earnings</strong> from your fund or ATO reporting when
                you have them — that is what the law taxes. Use the TSB estimate only when you do not yet have reported
                earnings.
                <span className="block mt-2 text-purple-900">
                    Headline rules are locked in (1 July 2026 start; realised earnings; indexed $3m / $10m; +15% / +10% tiers).
                    Member-level earnings attribution and some SMSF administrative detail remain <strong>subject to final regulations and ATO guidance</strong>.
                    Treat built or proxy earnings as provisional until you have reported fund figures.
                </span>
                {prefilledTsb.externalSuper > 0 && (
                    <span className="block mt-2 text-purple-900">
                        Prefilled TSB includes {fmt(prefilledTsb.externalSuper)} held outside this SMSF (from member external super fields).
                    </span>
                )}
            </WarningBox>

            <div className="border-b border-gray-200">
                <nav className="flex gap-1">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition ${
                                activeTab === tab.id
                                    ? 'bg-purple-700 text-white'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {activeTab === 'calculator' && (
                <div className="space-y-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">Division 296 earnings — {CURRENT_FY}</h3>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {[
                                { id: 'fund_builder', label: 'Build from fund return' },
                                { id: 'reported', label: 'Reported earnings' },
                                { id: 'proxy', label: 'Estimate from TSB movement' },
                            ].map(({ id, label }) => (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => setInputs(p => ({ ...p, earningsMode: id }))}
                                    className={`px-3 py-1.5 text-sm rounded-full border transition ${
                                        inputs.earningsMode === id
                                            ? 'bg-purple-700 text-white border-purple-700'
                                            : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        {inputs.earningsMode === 'fund_builder' && (
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600">
                                    Standard SMSF worksheet: taxable income − assessable contributions + net ECPI.
                                    Non-concessional contributions are not deducted.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {[
                                        {
                                            field: 'taxableIncome',
                                            label: 'Fund taxable income / (loss)',
                                            hint: 'From SMSF annual return — can be negative',
                                            signed: true,
                                        },
                                        {
                                            field: 'assessableContributions',
                                            label: 'Assessable contributions',
                                            hint: 'Concessional and other contributions in assessable income',
                                        },
                                        {
                                            field: 'grossEcpi',
                                            label: 'Gross ECPI',
                                            hint: 'Exempt current pension income before expense adjustments',
                                        },
                                        {
                                            field: 'ecpiNonDeductibleExpenses',
                                            label: 'ECPI non-deductible expenses',
                                            hint: 'Expenses denied because they relate to exempt pension income',
                                        },
                                    ].map(({ field, label, hint, signed }) => (
                                        <div key={field}>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                                            <p className="text-xs text-gray-500 mb-1">{hint}</p>
                                            <input
                                                type="text"
                                                value={
                                                    signed && inputs[field] < 0
                                                        ? `−$${Math.abs(inputs[field]).toLocaleString()}`
                                                        : `$${inputs[field].toLocaleString()}`
                                                }
                                                onChange={e => (signed ? setSigned(field, e.target.value) : set(field, e.target.value))}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                            />
                                        </div>
                                    ))}
                                </div>
                                {fundEarningsResult && (
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                        <h4 className="text-sm font-semibold text-gray-800 mb-2">Fund earnings breakdown</h4>
                                        <BreakdownRow label="Taxable income" value={fundEarningsResult.breakdown.taxableIncome} />
                                        <BreakdownRow label="Assessable contributions" value={fundEarningsResult.breakdown.assessableContributions} sign="minus" />
                                        <BreakdownRow label="Net ECPI" value={fundEarningsResult.breakdown.netEcpi} sign="plus" />
                                        <div className="flex justify-between text-sm font-semibold pt-2 mt-2 border-t border-gray-300">
                                            <span>Division 296 fund earnings</span>
                                            <span className="text-purple-800">{fmt(fundEarningsResult.earnings)}</span>
                                        </div>
                                        {fundEarningsResult.flooredFromNegative && (
                                            <p className="text-xs text-amber-700 mt-2">
                                                Raw earnings were {fmt(fundEarningsResult.rawEarnings)} — floored to $0 for Division 296.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        {inputs.earningsMode === 'reported' ? (
                            <div className="max-w-xl">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Division 296 fund earnings for {CURRENT_FY}
                                </label>
                                <p className="text-xs text-gray-500 mb-1">
                                    From your fund&apos;s Division 296 reporting or adviser — generally realised fund earnings
                                    under the ATO formula (not a TSB movement guess).
                                </p>
                                <input
                                    type="text"
                                    value={`$${inputs.division296Earnings.toLocaleString()}`}
                                    onChange={e => set('division296Earnings', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>
                        ) : inputs.earningsMode === 'proxy' ? (
                            <p className="text-sm text-gray-600">
                                Earnings will be estimated as TSB (end) − TSB (start) − contributions + benefits paid.
                                Use fund return or reported earnings when your accountant or ATO supplies them.
                            </p>
                        ) : null}
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">Total Super Balance — {CURRENT_FY}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                { field: 'tsbStart', label: 'TSB at 1 July 2026 (start of year)', hint: 'Total super across all funds at start of 2026-27' },
                                { field: 'tsbEnd', label: 'TSB at 30 June 2027 (end of year)', hint: 'Total super across all funds at end of 2026-27' },
                                { field: 'contributions', label: 'Total contributions made during year', hint: 'Concessional + non-concessional contributions across all funds' },
                                { field: 'benefitsPaid', label: 'Total benefits / withdrawals paid', hint: 'Pension payments, lump sum withdrawals, and commutations during the year' },
                            ].map(({ field, label, hint }) => (
                                <div key={field}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                                    <p className="text-xs text-gray-500 mb-1">{hint}</p>
                                    <input
                                        type="text"
                                        value={`$${inputs[field].toLocaleString()}`}
                                        onChange={e => set(field, e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={`rounded-xl p-6 ${result.isLiable ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                        <h3 className={`font-semibold text-lg mb-4 ${result.isLiable ? 'text-red-800' : 'text-green-800'}`}>
                            {result.isLiable
                                ? `Estimated Division 296 liability (${CURRENT_FY})`
                                : `No Division 296 liability on these inputs (${CURRENT_FY})`}
                        </h3>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            {[
                                {
                                    label: earningsModeLabel,
                                    value: fmt(result.superEarnings),
                                    sub: result.earningsSource === 'fund_builder'
                                        ? earningsSubLabel
                                        : result.earningsSource === 'reported'
                                            ? earningsSubLabel
                                            : earningsSubLabel,
                                },
                                { label: 'TSB for proportion', value: fmt(result.tsbForProportion), sub: `${CURRENT_FY} transitional: end-of-year TSB` },
                                { label: 'Proportion above LSBT', value: fmtPct(result.proportion), sub: `Above ${fmt(THRESHOLD)}` },
                                { label: 'Total Div 296 tax', value: fmt(result.taxAmount), sub: result.taxVeryLargeTier > 0 ? `Includes ${fmt(result.taxVeryLargeTier)} at 10% above VLSBT` : 'Division 296 liability', highlight: result.isLiable },
                            ].map(({ label, value, sub, highlight }) => (
                                <div key={label} className={`rounded-lg p-3 text-center ${highlight ? 'bg-red-100' : 'bg-white'}`}>
                                    <div className={`text-xl font-bold ${highlight ? 'text-red-700' : 'text-gray-900'}`}>{value}</div>
                                    <div className="text-xs font-medium text-gray-700 mt-1">{label}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">{sub}</div>
                                </div>
                            ))}
                        </div>

                        {result.isLiable && (
                            <>
                                <h4 className="font-semibold text-gray-800 mb-3">Payment options</h4>
                                <div className="bg-white rounded-lg p-4 mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Release from super: {inputs.releasePercent}% (max {RELEASE_MAX * 100}%)
                                    </label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={RELEASE_MAX * 100}
                                        value={inputs.releasePercent}
                                        onChange={e => setInputs(p => ({ ...p, releasePercent: Number(e.target.value) }))}
                                        className="w-full accent-purple-600"
                                    />
                                    <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                                        <div>
                                            <span className="text-gray-600">Released from super:</span>
                                            <span className="font-semibold text-red-700 ml-2">{fmt(releaseAmount)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Paid personally:</span>
                                            <span className="font-semibold text-amber-700 ml-2">{fmt(personalPayment)}</span>
                                        </div>
                                    </div>
                                </div>

                                <InfoBox>
                                    <strong>How payment works:</strong> The ATO issues a Division 296 assessment after
                                    you lodge your tax return. You may pay from personal funds, elect an ATO release
                                    authority so the tax is paid from super, or use a combination. Confirm current ATO
                                    election windows and due dates before relying on process timing.
                                </InfoBox>
                            </>
                        )}

                        {!result.isLiable && (
                            <p className="text-green-700 text-sm">
                                {result.superEarnings <= 0
                                    ? 'Fund earnings for this year are zero or negative. Division 296 tax only applies when earnings are positive.'
                                    : `Your Total Super Balance at year end (${fmt(inputs.tsbEnd)}) is at or below the ${fmt(THRESHOLD)} large super balance threshold. No Division 296 tax applies on these inputs.`
                                }
                            </p>
                        )}
                    </div>
                    <CalculationTransparency
                        kind="smsf"
                        title="Division 296 Transparency"
                        inputs={{
                            totalBalance: inputs.tsbEnd,
                            contributionAmount: inputs.assessableContributions,
                            fundBalance: inputs.tsbStart,
                        }}
                        results={{
                            ...result,
                            tableData: projection,
                            warnings: result.isLiable ? [{ message: 'Division 296 liability estimated.' }] : [],
                        }}
                    />
                </div>
            )}

            {activeTab === 'projection' && (
                <div className="space-y-4">
                    <InfoBox>
                        Projection reuses your {inputs.earningsMode === 'fund_builder' ? 'built fund' : inputs.earningsMode === 'reported' ? 'reported' : 'estimated'} earnings
                        each year (except balance growth affects TSB proportion). Assumes 7% annual investment growth,
                        same contributions and withdrawals. Thresholds shown are 2026-27 values; LSBT and VLSBT index to CPI
                        in $150,000 and $500,000 steps respectively.
                    </InfoBox>

                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-purple-700 text-white">
                                <tr>
                                    {['Financial year', 'Opening balance', 'Closing balance', 'Earnings used', 'Proportion above LSBT', 'Div 296 tax'].map(h => (
                                        <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {projection.map((row, i) => (
                                    <tr key={row.fy} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-4 py-3 font-medium">{row.fy}</td>
                                        <td className="px-4 py-3">{fmt(row.startBal)}</td>
                                        <td className="px-4 py-3">{fmt(row.endBal)}</td>
                                        <td className="px-4 py-3">{fmt(row.earnings)}</td>
                                        <td className="px-4 py-3">{fmtPct(row.proportion)}</td>
                                        <td className={`px-4 py-3 font-semibold ${row.isLiable ? 'text-red-700' : 'text-green-700'}`}>
                                            {row.isLiable ? fmt(row.taxAmount) : 'Nil'}
                                        </td>
                                    </tr>
                                ))}
                                <tr className="bg-purple-50 border-t-2 border-purple-200 font-semibold">
                                    <td className="px-4 py-3" colSpan={5}>Total Division 296 tax over 5 years</td>
                                    <td className="px-4 py-3 text-red-700">
                                        {fmt(projection.reduce((sum, r) => sum + r.taxAmount, 0))}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'explainer' && (
                <div className="space-y-4 text-sm text-gray-700">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
                        <h3 className="font-semibold text-gray-900 text-base">How Division 296 tax is calculated</h3>

                        <div>
                            <h4 className="font-semibold text-purple-800 mb-1">Step 1 — Division 296 earnings</h4>
                            <p>
                                Final law uses Division 296 fund earnings reported through the ATO framework (generally
                                realised earnings). Use <strong>Build from fund return</strong> with your SMSF annual return
                                figures, <strong>Reported earnings</strong> when you have the ATO or fund number, or{' '}
                                <strong>Estimate from TSB movement</strong> as a directional proxy only:
                            </p>
                            <div className="bg-gray-50 rounded-lg p-3 font-mono text-xs mb-2 mt-2">
                                Fund earnings = taxable income − assessable contributions + net ECPI<br />
                                Proxy earnings = TSB (end) − TSB (start) − contributions + benefits paid
                            </div>
                            <p>
                                Unrealised capital gains are not taxed under the enacted law. Confirm how your funds
                                report Division 296 earnings on the ATO website.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-semibold text-purple-800 mb-1">Step 2 — Proportions above thresholds</h4>
                            <div className="bg-gray-50 rounded-lg p-3 font-mono text-xs mb-2">
                                Proportion above LSBT = (TSB for proportion − LSBT) / TSB for proportion<br />
                                Proportion above VLSBT = (TSB for proportion − VLSBT) / TSB for proportion
                            </div>
                            <p>
                                For 2026-27 the ATO uses end-of-year TSB for proportion tests. From later years the
                                greater of your TSB immediately before the financial year and at 30 June may apply.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-semibold text-purple-800 mb-1">Step 3 — Apply tax rates</h4>
                            <div className="bg-gray-50 rounded-lg p-3 font-mono text-xs mb-2">
                                Tax = 15% × earnings × proportion above LSBT<br />
                                + 10% × earnings × proportion above VLSBT (if applicable)
                            </div>
                        </div>

                        <div>
                            <h4 className="font-semibold text-purple-800 mb-1">Key differences from Division 293</h4>
                            <table className="w-full text-xs border border-gray-200 rounded">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Feature</th>
                                        <th className="px-3 py-2 text-left">Division 293</th>
                                        <th className="px-3 py-2 text-left">Division 296</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        ['Trigger', 'Income for surcharge purposes > $250,000', 'TSB above LSBT ($3M for 2026-27)'],
                                        ['Tax base', 'Concessional contributions', 'Division 296 fund earnings (proportion above thresholds)'],
                                        ['Rate', '15% additional', '15% above LSBT + 10% above VLSBT'],
                                        ['Indexed?', 'No — $250k since 2012', 'Yes — LSBT and VLSBT CPI-indexed'],
                                        ['Applies from', '2012-13', '2026-27'],
                                        ['Payment', 'Personal or release authority', 'Personal or release authority'],
                                    ].map(([feat, d293, d296], i) => (
                                        <tr key={feat} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td className="px-3 py-2 font-medium">{feat}</td>
                                            <td className="px-3 py-2">{d293}</td>
                                            <td className="px-3 py-2">{d296}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm">
                        <strong>General information only:</strong> SuperCalc Pro does not hold an AFSL. This calculator
                        is an educational estimate. Division 296 rules, TSB definitions, and reporting change with ATO
                        guidance. Consult a licensed tax adviser before acting on these numbers.
                    </div>
                </div>
            )}

            <div className="bg-white p-6 rounded-lg shadow-md mt-6">
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Division 296 Analysis as of: {new Date().toLocaleDateString()}
                    </div>
                    <button
                        type="button"
                        onClick={() => SMSFExportEngine.exportToPDF('division296', {
                            ...inputs,
                            division296Earnings: result.superEarnings,
                            superEarnings: result.superEarnings,
                            useTsbProxy: inputs.earningsMode === 'proxy',
                            financialYear: CURRENT_FY,
                            earningsSource: inputs.earningsMode,
                            fundName: smsfState.smsfData?.fundName,
                        })}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                        Export Division 296 Report (PDF)
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SMSFDivision296Calculator;
